// AN-S126a breathalyzer via WebUSB (CH340 USB-serial chip)
// Works directly in the browser - no backend serial port needed

const CH340_VID = 0x1A86;
const CH340_PID = 0x7523;

const CMD_INIT    = new Uint8Array([0xAA, 0x13, 0xFF, 0x12]);
const CMD_RESET   = new Uint8Array([0xAA, 0xF0, 0x00, 0xF0]);
const CMD_MEASURE = new Uint8Array([0xAA, 0x11, 0xFF, 0x10]);

const STATE_WARMUP  = 0x10;
const STATE_WAITING = 0x11;
const STATE_RESULT  = 0x12;

export type AlcoholUSBEvent =
  | { type: 'warming';  countdown: number }
  | { type: 'waiting' }
  // value は検知器の生バイト（1 = 0.001 mg/L）。変換は services/alcohol-value.ts で行う。
  | { type: 'done';    value: number }
  | { type: 'debug';   message: string }
  | { type: 'error';   message: string };

export function isWebUSBSupported(): boolean {
  return typeof navigator !== 'undefined' && 'usb' in navigator;
}

function calcCH340Baud(baud: number): number {
  let factor = 1532620800 / baud;
  let divisor = 3;
  while (factor > 0xfff0 && divisor > 0) {
    factor = Math.floor(factor / 8);
    divisor--;
  }
  factor = 0x10000 - Math.floor(factor);
  return (factor & 0xff00) | divisor;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UsbDevice = any;

async function controlOut(dev: UsbDevice, request: number, value: number, index: number) {
  await dev.controlTransferOut(
    { requestType: 'vendor', recipient: 'device', request, value, index }
  );
}

async function initCH340(dev: UsbDevice) {
  await dev.controlTransferIn(
    { requestType: 'vendor', recipient: 'device', request: 0x5F, value: 0, index: 0 }, 2
  );
  await controlOut(dev, 0xA1, 0, 0);
  await controlOut(dev, 0x9A, 0x1312, calcCH340Baud(9600));
  // 8N1: enable RX(0x80) + TX(0x40) + 8-bit(0x03)
  await controlOut(dev, 0x9A, 0x2518, 0xC3);
  // DTR + RTS
  await controlOut(dev, 0xA4, 0xFF9F, 0);
}

// Find bulk IN and OUT endpoint numbers from the USB descriptor
function findBulkEndpoints(dev: UsbDevice): { epIn: number; epOut: number } | null {
  try {
    const iface = dev.configuration?.interfaces?.[0]?.alternate;
    if (!iface) return null;
    let epIn = -1, epOut = -1;
    for (const ep of iface.endpoints) {
      if (ep.type === 'bulk') {
        if (ep.direction === 'in')  epIn  = ep.endpointNumber;
        if (ep.direction === 'out') epOut = ep.endpointNumber;
      }
    }
    if (epIn < 0 || epOut < 0) return null;
    return { epIn, epOut };
  } catch {
    return null;
  }
}

export async function startAlcoholUSB(
  onEvent: (ev: AlcoholUSBEvent) => void
): Promise<() => void> {
  let dev: UsbDevice = null;
  let stopped = false;

  const cleanup = () => {
    stopped = true;
    if (dev) {
      dev.releaseInterface(0).catch(() => {});
      dev.close().catch(() => {});
      dev = null;
    }
  };

  try {
    // Try previously authorized device first, otherwise prompt user
    const existing = await (navigator as any).usb.getDevices() as UsbDevice[];
    dev = existing.find((d: UsbDevice) => d.vendorId === CH340_VID && d.productId === CH340_PID) ?? null;

    if (!dev) {
      dev = await (navigator as any).usb.requestDevice({
        filters: [{ vendorId: CH340_VID, productId: CH340_PID }],
      });
    }

    await dev.open();
    if (dev.configuration === null) await dev.selectConfiguration(1);
    await dev.claimInterface(0);

    // Detect actual bulk endpoint numbers from USB descriptor
    // CH340G standard: EP1=Bulk-IN (device→host), EP2=Bulk-OUT (host→device)
    const eps = findBulkEndpoints(dev);
    const epOut = eps?.epOut ?? 2;
    const epIn  = eps?.epIn  ?? 1;

    onEvent({ type: 'debug', message: `VID=${dev.vendorId.toString(16)} PID=${dev.productId.toString(16)} epIN=${epIn} epOUT=${epOut}` });

    await initCH340(dev);
    onEvent({ type: 'debug', message: 'CH340 init OK, sending CMD_INIT' });

    // Send AN-S126a init sequence
    await dev.transferOut(epOut, CMD_INIT);
    setTimeout(async () => { if (!stopped) await dev.transferOut(epOut, CMD_RESET);   }, 300);
    setTimeout(async () => { if (!stopped) await dev.transferOut(epOut, CMD_MEASURE); }, 1500);

    // Packet parser
    let rxBuf = new Uint8Array(0);

    const processBuffer = () => {
      while (rxBuf.length >= 4) {
        if (rxBuf[0] === 0xAA) {
          const state = rxBuf[1];
          const value = rxBuf[2];
          if (state === STATE_RESULT) {
            stopped = true;
            onEvent({ type: 'done', value });
            cleanup();
            return;
          }
          if (state === STATE_WARMUP)  onEvent({ type: 'warming', countdown: value });
          if (state === STATE_WAITING) onEvent({ type: 'waiting' });
          rxBuf = rxBuf.slice(4);
        } else {
          rxBuf = rxBuf.slice(1);
        }
      }
    };

    // Read loop
    (async () => {
      while (!stopped) {
        try {
          const result = await dev.transferIn(epIn, 64);
          if (result.status !== 'ok' || !result.data || result.data.byteLength === 0) continue;
          const chunk = new Uint8Array(result.data.buffer);
          const merged = new Uint8Array(rxBuf.length + chunk.length);
          merged.set(rxBuf);
          merged.set(chunk, rxBuf.length);
          rxBuf = merged;
          processBuffer();
        } catch {
          if (!stopped) onEvent({ type: 'error', message: 'デバイス読み込みエラー' });
          break;
        }
      }
    })();

    const timer = setTimeout(() => {
      if (!stopped) {
        onEvent({ type: 'error', message: 'タイムアウト' });
        cleanup();
      }
    }, 60000);

    return () => { clearTimeout(timer); cleanup(); };

  } catch (e: unknown) {
    cleanup();
    const msg = e instanceof Error ? e.message : 'デバイスに接続できません';
    // NotFoundError = user cancelled the device picker, treat as cancel not error
    if (e instanceof Error && e.name === 'NotFoundError') {
      onEvent({ type: 'error', message: 'cancelled' });
      return () => {};
    }
    onEvent({ type: 'error', message: msg });
    return () => {};
  }
}
